/**
 * Module (domain) metadata for the in-app notification centre.
 *
 * One row per notification domain returned by the template catalogue. The
 * render order is the backend's `DOMAIN_DISPLAY_ORDER`, re-exported from the
 * API barrel as {@link NOTIFICATION_DOMAIN_ORDER} — nothing here re-invents it.
 *
 * Each row also declares how the module is gated for the acting user:
 *  - `sitePermissionFlag` — the `UserSitePermission` boolean that grants the
 *    module at a site (the same flag the backend audience rules read);
 *  - `menuKeys` — the tenant `MenuVisibility` keys; a domain is hidden when the
 *    tenant has switched the module off entirely.
 *
 * @link useNotificationModuleAccess for how these gates are combined.
 */
import {
  AlertTriangle,
  Bell,
  FileText,
  Package,
  Send,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

import { NOTIFICATION_DOMAIN_ORDER, type NotificationDomain } from '../../lib/api';

/** The `UserSitePermission` flag that grants a module, `''` when ungated. */
export type NotificationModuleFlag =
  | 'can_purchasing'
  | 'can_quality'
  | 'can_compliance'
  | '';

export interface NotificationDomainMeta {
  domain: NotificationDomain;
  /** Section heading, e.g. "Documents & Compliance". */
  label: string;
  /** One-line description shown when a section is empty. */
  emptyHint: string;
  icon: LucideIcon;
  sitePermissionFlag: NotificationModuleFlag;
  /** Tenant menu keys — the module is visible when ANY of them is on. */
  menuKeys: string[];
  /**
   * Never hidden by role/module gating. System messages (announcements,
   * account and security notices) must reach every user.
   */
  alwaysVisible: boolean;
}

export const NOTIFICATION_DOMAIN_META: Record<NotificationDomain, NotificationDomainMeta> = {
  purchasing: {
    domain: 'purchasing',
    label: 'Purchasing',
    emptyHint: 'No purchasing notifications',
    icon: Package,
    sitePermissionFlag: 'can_purchasing',
    menuKeys: ['purchasing', 'purchaseOrders'],
    alwaysVisible: false,
  },
  quality: {
    domain: 'quality',
    label: 'Quality',
    emptyHint: 'No quality notifications',
    icon: AlertTriangle,
    sitePermissionFlag: 'can_quality',
    menuKeys: ['quality'],
    alwaysVisible: false,
  },
  compliance: {
    domain: 'compliance',
    label: 'Documents & Compliance',
    emptyHint: 'No document or compliance notifications',
    icon: FileText,
    // No site flag: whoever can open Documents must see its notifications.
    // `can_compliance` is not what grants the Documents page, so gating on it
    // hid document bells from almost every user while their emails still went.
    sitePermissionFlag: '',
    menuKeys: ['documents', 'supplierDocuments'],
    alwaysVisible: false,
  },
  onboarding: {
    domain: 'onboarding',
    label: 'Onboarding',
    emptyHint: 'No onboarding notifications',
    icon: UserPlus,
    sitePermissionFlag: '',
    menuKeys: ['supplierOnboarding'],
    alwaysVisible: false,
  },
  rfq: {
    domain: 'rfq',
    label: 'RFQ',
    emptyHint: 'No RFQ notifications',
    icon: Send,
    sitePermissionFlag: '',
    menuKeys: ['rfq'],
    alwaysVisible: false,
  },
  system: {
    domain: 'system',
    label: 'System',
    emptyHint: 'No system notifications',
    icon: Bell,
    sitePermissionFlag: '',
    menuKeys: [],
    alwaysVisible: true,
  },
};

/** Domains in render order — Purchasing, Quality, Compliance, Onboarding, RFQ, System. */
export const NOTIFICATION_DOMAINS: readonly NotificationDomain[] = NOTIFICATION_DOMAIN_ORDER;

/** Position of a domain in the fixed render order (unknown domains sort last). */
export function domainOrder(domain: NotificationDomain): number {
  const index = NOTIFICATION_DOMAINS.indexOf(domain);
  return index === -1 ? NOTIFICATION_DOMAINS.length : index;
}

/** Narrow an arbitrary string to a known domain slug. */
export function isNotificationDomain(value: string): value is NotificationDomain {
  return Object.prototype.hasOwnProperty.call(NOTIFICATION_DOMAIN_META, value);
}

/** Metadata for a domain, falling back to System for anything unrecognised. */
export function getDomainMeta(domain: string): NotificationDomainMeta {
  return isNotificationDomain(domain)
    ? NOTIFICATION_DOMAIN_META[domain]
    : NOTIFICATION_DOMAIN_META.system;
}
